import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Shield,
  Zap,
  LayoutGrid,
  Puzzle,
  Globe,
  Terminal,
  ArrowUpRight,
} from "lucide-react";
import { contributionBadges } from "../data/mock";
import { SectionReveal } from "./SectionReveal";

const iconMap = {
  Shield,
  Zap,
  Layout: LayoutGrid,
  Puzzle,
  Globe,
  Terminal,
};

const BadgeCard = ({ badge, index, isVisible }) => {
  const Icon = iconMap[badge.icon] || Shield;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className="group bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl p-5 hover:border-emerald-500/30 dark:hover:border-emerald-500/20 hover:bg-emerald-50/50 dark:hover:bg-zinc-900/80 transition-colors duration-300 shadow-sm dark:shadow-none"
    >
      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/15 flex items-center justify-center mb-4 group-hover:bg-emerald-500/15 transition-colors">
        <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
      </div>
      <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold text-sm">{badge.title}</h3>
      <p className="text-zinc-500 text-xs mt-2 leading-relaxed">
        {badge.description}
      </p>
    </motion.div>
  );
};

const OpenSourceSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      id="opensource"
      className="relative py-28 lg:py-36 bg-zinc-50 dark:bg-zinc-950 transition-colors duration-500"
      ref={sectionRef}
      aria-label="Open Source Contributions"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionReveal animation="blurIn">
          <div className="mb-16">
            <span className="text-sm font-mono text-emerald-600/80 dark:text-emerald-400/80 tracking-widest uppercase">
              Open Source
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">
              Contributing to the{" "}
              <span className="text-emerald-600 dark:text-emerald-400">ecosystem</span>
            </h2>
            <p className="mt-4 text-zinc-500 text-lg max-w-2xl">
              Active contributor across WordPress Core, Gutenberg, WP-CLI,
              translations, and the broader WordPress open-source ecosystem.
            </p>
          </div>
        </SectionReveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-16">
          {contributionBadges.map((badge, i) => (
            <BadgeCard key={badge.title} badge={badge} index={i} isVisible={isInView} />
          ))}
        </div>

        <SectionReveal animation="scaleUp" delay={0.3}>
          <div className="text-center">
            <a
              href="https://profiles.wordpress.org/hbhalodia/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800/50 rounded-xl text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-500/30 dark:hover:border-emerald-500/20 transition-colors duration-300 shadow-sm dark:shadow-none"
            >
              View full WordPress.org profile
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default OpenSourceSection;
